// src/components/script-analysis/ScriptScoreResult.jsx
import React from "react";

const ScoreBar = ({ score, theme }) => {
  const pct = Math.max(0, Math.min(100, ((score - 6) / 4) * 100));
  const color = score >= 8.5 ? "var(--color-success)" : score >= 8.0 ? theme.colors.accentPrimary : score >= 7.5 ? "var(--color-warning)" : "var(--color-error)";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color }}>{score}</span>
    </div>
  );
};

const OrientationBadge = ({ value }) => {
  const map = {
    "正面积极": { bg: "rgba(61,158,90,0.15)", color: "var(--color-success)" },
    "中性": { bg: "rgba(212,167,68,0.15)", color: "var(--color-warning)" },
    "偏负面": { bg: "rgba(217,58,58,0.15)", color: "var(--color-error)" },
    "出色": { bg: "rgba(61,158,90,0.15)", color: "var(--color-success)" },
    "尚可": { bg: "rgba(212,167,68,0.15)", color: "var(--color-warning)" },
    "欠缺": { bg: "rgba(217,58,58,0.15)", color: "var(--color-error)" },
  };
  const s = map[value] || map["中性"];
  return <span className="text-2xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: s.bg, color: s.color }}>{value}</span>;
};

const StrengthsWeaknesses = ({ strengths, weaknesses }) => (
  <div className="grid grid-cols-2 gap-3 mt-3">
    {strengths?.length > 0 && (
      <div className="rounded-lg p-3" style={{ backgroundColor: "color-mix(in srgb, var(--color-success) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--color-success) 15%, transparent)" }}>
        <div className="text-2xs font-semibold mb-1.5" style={{ color: "var(--color-success)" }}>优点</div>
        {strengths.map((s, i) => <p key={i} className="text-2xs leading-relaxed mb-1" style={{ color: "var(--color-text-secondary)" }}>· {s}</p>)}
      </div>
    )}
    {weaknesses?.length > 0 && (
      <div className="rounded-lg p-3" style={{ backgroundColor: "color-mix(in srgb, var(--color-error) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--color-error) 15%, transparent)" }}>
        <div className="text-2xs font-semibold mb-1.5" style={{ color: "var(--color-error)" }}>缺点</div>
        {weaknesses.map((w, i) => <p key={i} className="text-2xs leading-relaxed mb-1" style={{ color: "var(--color-text-secondary)" }}>· {w}</p>)}
      </div>
    )}
  </div>
);

const DimensionRow = ({ label, item, theme }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>{label}</span>
    </div>
    <ScoreBar score={item?.score || 0} theme={theme} />
    <p className="text-2xs mt-1 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{item?.analysis}</p>
  </div>
);

const Section = ({ title, badge, children }) => (
  <div className="rounded-xl p-5 border mb-4" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
    <div className="flex items-center gap-2 mb-3 pb-2 border-b" style={{ borderColor: "var(--color-border-primary)" }}>
      <h3 className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</h3>
      {badge}
    </div>
    {children}
  </div>
);

const ScriptScoreResult = ({ data, theme }) => {
  if (!data) return null;
  const overall = data.overall || {};

  return (
    <div className="space-y-4">
      {/* Overall */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>总体评价</span>
          <span className="text-2xl font-black" style={{ color: theme.colors.accentPrimary }}>{overall.score || "-"}</span>
        </div>
        <p className="text-xs leading-relaxed mb-3" style={{ color: "var(--color-text-secondary)" }}>{overall.analysis}</p>
        {overall.suggestion && (
          <div className="px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: theme.colors.accentLight, color: theme.colors.accentPrimary }}>
            {overall.suggestion}
          </div>
        )}
      </div>

      {/* Ideology */}
      {data.ideology && (
        <Section title="思想性" badge={<OrientationBadge value={data.ideology.overall} />}>
          {data.ideology.values && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>价值观</span>
                <OrientationBadge value={data.ideology.values.orientation} />
              </div>
              <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{data.ideology.values.analysis}</p>
            </div>
          )}
          {data.ideology.socialSignificance && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>社会意义</span>
                <OrientationBadge value={data.ideology.socialSignificance.orientation} />
              </div>
              <p className="text-2xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{data.ideology.socialSignificance.analysis}</p>
            </div>
          )}
          <StrengthsWeaknesses strengths={data.ideology.strengths} weaknesses={data.ideology.weaknesses} />
        </Section>
      )}

      {/* Artistry */}
      {data.artistry && (
        <Section title="艺术性" badge={<OrientationBadge value={data.artistry.overall} />}>
          <DimensionRow label="细节刻画" item={data.artistry.detailDepiction} theme={theme} />
          <DimensionRow label="创意呈现" item={data.artistry.creativePresentation} theme={theme} />
          <DimensionRow label="叙事逻辑" item={data.artistry.narrativeLogic} theme={theme} />
          <DimensionRow label="叙事技巧" item={data.artistry.narrativeTechnique} theme={theme} />
          <DimensionRow label="叙事节奏" item={data.artistry.narrativeRhythm} theme={theme} />
          <DimensionRow label="台词表达" item={data.artistry.dialogue} theme={theme} />
          <StrengthsWeaknesses strengths={data.artistry.strengths} weaknesses={data.artistry.weaknesses} />
        </Section>
      )}

      {/* Watchability */}
      {data.watchability && (
        <Section title="观赏性">
          <DimensionRow label="受众基础" item={data.watchability.audienceBase} theme={theme} />
          <DimensionRow label="话题性" item={data.watchability.topicality} theme={theme} />
          <DimensionRow label="题材风格" item={data.watchability.genreStyle} theme={theme} />
          <DimensionRow label="人物塑造" item={data.watchability.characterDevelopment} theme={theme} />
          <DimensionRow label="人物关系" item={data.watchability.relationships} theme={theme} />
          <DimensionRow label="情节桥段" item={data.watchability.plotSegments} theme={theme} />
          <StrengthsWeaknesses strengths={data.watchability.strengths} weaknesses={data.watchability.weaknesses} />
        </Section>
      )}
    </div>
  );
};

export default ScriptScoreResult;
